import { Component, effect, inject, OnDestroy, untracked, viewChildren } from '@angular/core';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { VendorStore } from '@apps/mtjr/vendors/app/vendor/list/vendors.page';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonItem,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxBackButtonComponent } from '@libs/core/lib/form-controls/mtjr-back-button.component';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { errorState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { VxAttachmentFieldComponent } from '@libs/mtjr/shared/attachment/attachment-field.component';
import { VendorUpdateDtoMap } from '@libs/mtjr/shared/generated/dtoMaps/vendor-update.dto.map';
import { withVendorDelete, withVendorUpdate } from '@libs/mtjr/shared/generated/entities/vendor/vendor-store-features';
import { vendorUpdateUtils } from '@libs/mtjr/shared/generated/forms';
import { UserStore } from '@libs/mtjr/shared/user/user.store';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { plainToInstance } from 'class-transformer';
import { createPatch } from 'rfc6902';
import { Subscription } from 'rxjs';

const VendorEditStore = signalStore(
  withVendorUpdate(),
  withVendorDelete(),
  withMethods((store) => ({
    setFailure(failure: Failure) {
      patchState(store, { vendorUpdateState: errorState(failure) });
    },
  })),
);

@Component({
  selector: 'mav-store-edit',
  templateUrl: './vendor-edit.page.html',
  providers: [VendorEditStore],
  imports: [
    IonTitle,
    IonBackButton,
    IonButtons,
    IonToolbar,
    IonHeader,
    IonContent,
    ReactiveFormsModule,
    FormsModule,
    VxTextFieldComponent,
    VxSubmitButtonComponent,
    VxBackButtonComponent,
    VxErrorBarComponent,
    TranslatePipe,
    VxAttachmentFieldComponent,
    IonCard,
    IonItem,
    IonText,
    IonButton,
    RouterLink,
    VxLoadingComponent,
    VxErrorComponent,
  ],
})
export class VendorEditPage implements OnDestroy {
  editStore = inject(VendorEditStore);
  store = inject(VendorStore);
  userStore = inject(UserStore);
  translateService = inject(TranslateService);
  updateUtils = vendorUpdateUtils(inject(NonNullableFormBuilder));
  route = inject(ActivatedRoute);
  router = inject(Router);
  alerts = inject(AppAlerts);
  form = this.updateUtils.form;
  translate = inject(TranslateService);
  vendorId: string = this.route.snapshot.params['vendorId'];
  attachments = viewChildren(VxAttachmentFieldComponent);
  modalCtr = inject(ModalController);
  eventBus = inject(NgEventBus);
  routeSub?: Subscription;

  ionViewWillLeave() {
    this.modalCtr.dismiss();
  }
  constructor() {
    this.store.getVendorById({ withDraft: true }, { vendorId: this.vendorId });
    this.userStore.fetchUserIfNeeded();

    effect(() => {
      const state = this.store.getVendorByIdState();
      if (state.success) {
        this.updateUtils.form.patchValue(state.data);
      }
    });

    effect(() => {
      console.log('Vendor update state:', this.editStore.vendorUpdateState());
      const state = this.editStore.vendorUpdateState();
      if (state.success) {
        untracked(() => {
          this.alerts.successSnackbarTR('VENDORS.VENDOR_UPDATE_SUCCESS');
          this.router.navigate(['/main/vendors', this.vendorId]);
          this.eventBus.cast('vendors:fetch', { vendorId: this.vendorId });
        });
      }
    });

    effect(() => {
      const state = this.editStore.vendorDeleteState();
      if (state.success) {
        untracked(() => {
          this.alerts.successSnackbarTR('VENDORS.VENDOR_DELETE_SUCCESS');
          this.router.navigate(['/main/vendors']);
          this.eventBus.cast('vendors:fetch', { vendorId: this.vendorId });
        });
      }
    });

    this.routeSub = this.route.queryParams.subscribe((params) => {
      const selectedAddrId = params['selectedAddrId'];
      if (selectedAddrId) {
        const address = this.userStore.addresses().find((addr) => addr.id === selectedAddrId);
        if (address) {
          this.updateUtils.form.patchValue({
            addressLabel: address.label,
            addressStreet: address.street,
            addressInternal: address.internalAddress,
            addressPhone: address.phone,
            addressPhone2: address.phone2,
            addressLat: address.latitude,
            addressLng: address.longitude,
          });
          this.updateUtils.form.markAsDirty();
        }
        this.router.navigate([], {
          queryParams: { selectedAddrId: null },
          relativeTo: this.route,
          replaceUrl: true,
          queryParamsHandling: 'merge',
        });
      }
    });
  }

  async deleteVendor() {
    const confirm = await this.alerts.yesNoAlert({
      header: `${this.translate.instant('COMMON.DELETE')} (${this.store.vendor()?.name})`,
      message: this.translateService.instant('VENDORS.VENDOR_DELETE_CONFIRM'),
      yesCaption: this.translateService.instant('VENDORS.VENDOR_DELETE'),
      noCaption: this.translateService.instant('COMMON.CANCEL'),
    });

    if (confirm) {
      await this.editStore.vendorDelete({ vendorId: this.vendorId });
    }
  }

  submit() {
    if (this.updateUtils.form.valid) {
      const vendor = this.store.vendor();

      if (!vendor) {
        this.editStore.setFailure(new LocalFailure());
      }

      const result = this.updateUtils.parse();
      if (!result.success) {
        this.editStore.setFailure(new LocalFailure());
        this.updateUtils.form.markAllAsTouched();
        return;
      }
      const vendorDto = plainToInstance(VendorUpdateDtoMap, vendor, {
        excludeExtraneousValues: true,
      });
      const dto = plainToInstance(VendorUpdateDtoMap, result.data, {
        excludeExtraneousValues: true,
      });
      const operations = createPatch(vendorDto, dto);
      console.log('Vendor update operations:', operations);

      if (operations.length === 0) {
        this.router.navigate(['/main/vendors', this.vendorId]);
        this.alerts.snackbarTR('COMMON.NO_CHANGES_MADE');
        return;
      }
      console.log('Submitting vendor update:', result.data);

      if (this.attachments().some((a) => a.hasPendingUploads())) {
        this.alerts.dangerSnackbarTR('COMMON.ATTACHMENTS_UPLOAD_IN_PROGRESS');
        return;
      }

      this.editStore.vendorUpdate({ vendor: dto }, { vendorId: this.vendorId });
    } else {
      this.updateUtils.form.markAllAsTouched();
    }
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }
}
